/**
 * Single source of truth for chain / RPC / token / contract addresses.
 *
 * Every value can be overridden via VITE_* env vars. Defaults are the Base Sepolia
 * demo deployment. Frame Network values are intentionally left as TBD placeholders:
 * do NOT fill them in until Frame publishes official chain ID / RPC / token address.
 * See docs/MAINNET_CHECKLIST.md.
 */

export type NetworkKey = 'baseSepolia' | 'base' | 'frame';

export interface NetworkConfig {
  key: NetworkKey;
  /** hex chain id for wallet_switchEthereumChain; '' when TBD */
  chainId: string;
  chainIdDecimal: number | null;
  chainName: string;
  nativeCurrency: { name: string; symbol: string; decimals: number };
  rpcUrls: string[];
  blockExplorerUrls: string[];
  /** default contract addresses for this network ('' when TBD) */
  gameAddress: string;
  frameTokenAddress: string;
  teamPowersAddress: string;
  /** true if values are placeholders and the network is not usable yet */
  tbd?: boolean;
}

export const NETWORKS: Record<NetworkKey, NetworkConfig> = {
  baseSepolia: {
    key: 'baseSepolia',
    chainId: '0x14a34', // 84532
    chainIdDecimal: 84532,
    chainName: 'Base Sepolia',
    nativeCurrency: { name: 'Sepolia Ether', symbol: 'ETH', decimals: 18 },
    rpcUrls: ['https://sepolia.base.org'],
    blockExplorerUrls: ['https://sepolia.basescan.org'],
    gameAddress: '0x9D7AF452af7BD647fEf0Ee4CB3DF103babB58A86', // diamond
    frameTokenAddress: '0xcCeBB12DCC2F7e71Ee2CB8Ad491D6CC57121192A', // mock FRAME
    teamPowersAddress: '0xB68948c6e2c0B252D1A47bf50C30e4C5253B4056',
  },
  base: {
    key: 'base',
    chainId: '0x2105', // 8453
    chainIdDecimal: 8453,
    chainName: 'Base',
    nativeCurrency: { name: 'Ethereum', symbol: 'ETH', decimals: 18 },
    rpcUrls: ['https://mainnet.base.org'],
    blockExplorerUrls: ['https://basescan.org'],
    gameAddress: '',
    frameTokenAddress: '',
    teamPowersAddress: '',
  },
  frame: {
    key: 'frame',
    // TBD: Frame Network chain ID, RPC, explorer and $FRAME token are not published yet.
    chainId: '', // TBD
    chainIdDecimal: null, // TBD
    chainName: 'Frame Network (TBD)',
    nativeCurrency: { name: 'TBD', symbol: 'TBD', decimals: 18 },
    rpcUrls: [], // TBD
    blockExplorerUrls: [], // TBD
    gameAddress: '', // TBD (deploy diamond after audit)
    frameTokenAddress: '', // TBD (official $FRAME token)
    teamPowersAddress: '', // TBD
    tbd: true,
  },
};

const env = import.meta.env;

function pickNetwork(): NetworkConfig {
  const explicit = env.VITE_NETWORK as NetworkKey | undefined;
  if (explicit && NETWORKS[explicit]) return NETWORKS[explicit];
  if (env.VITE_TESTNET === 'true' || env.VITE_RPC_URL?.includes('sepolia')) return NETWORKS.baseSepolia;
  // Default stays testnet until mainnet config is filled in
  return env.VITE_GAME_ADDRESS ? NETWORKS.base : NETWORKS.baseSepolia;
}

const base = pickNetwork();

/** Active network, with env overrides applied. Import this instead of reading VITE_* directly. */
export const CHAIN = {
  ...base,
  rpcUrl: env.VITE_RPC_URL || base.rpcUrls[0] || '',
  gameAddress: env.VITE_GAME_ADDRESS || base.gameAddress,
  frameTokenAddress: env.VITE_FRAME_TOKEN_ADDRESS || env.VITE_PAW_TOKEN_ADDRESS || base.frameTokenAddress,
  teamPowersAddress: env.VITE_TEAM_POWERS_ADDRESS || base.teamPowersAddress,
  explorerUrl: base.blockExplorerUrls[0] || 'https://basescan.org',
  isTestnet: base.key === 'baseSepolia',
};

/** Shape accepted by wallet_addEthereumChain */
export function toAddChainParams(n: NetworkConfig = base) {
  return {
    chainId: n.chainId,
    chainName: n.chainName,
    nativeCurrency: n.nativeCurrency,
    rpcUrls: n.rpcUrls,
    blockExplorerUrls: n.blockExplorerUrls,
  };
}

/** Real-money play stays hidden until mainnet is configured and legal review is done. */
export const REAL_MONEY_ENABLED = env.VITE_REAL_MONEY_ENABLED === 'true' && !base.tbd;
